import { EmailBlock, EmailGlobalStyles, DEFAULT_EMAIL_STYLES } from './types'

export function generateEmailHtml(
  blocks: EmailBlock[],
  styles: EmailGlobalStyles = DEFAULT_EMAIL_STYLES,
): string {
  const lines: string[] = []

  lines.push('<!DOCTYPE html>')
  lines.push('<html lang="en" xmlns="http://www.w3.org/1999/xhtml">')
  lines.push('<head>')
  lines.push('<meta charset="utf-8" />')
  lines.push(
    '<meta name="viewport" content="width=device-width, initial-scale=1.0" />',
  )
  lines.push('<title>Email</title>')
  lines.push(
    '<!--[if mso]><style>table,td{font-family:Arial,sans-serif;}</style><![endif]-->',
  )
  lines.push('<style>')
  lines.push(
    `body{margin:0;padding:0;background-color:${styles.backgroundColor};-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}`,
  )
  lines.push(
    `table{border-spacing:0;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;}`,
  )
  lines.push(
    `img{border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;display:block;}`,
  )
  lines.push(`a{color:${styles.linkColor};text-decoration:underline;}`)
  lines.push(
    `@media only screen and (max-width:620px){.email-container{width:100%!important;}.stack-col{display:block!important;width:100%!important;}}`,
  )
  lines.push('</style>')
  lines.push('</head>')
  lines.push(
    `<body style="margin:0;padding:0;background-color:${styles.backgroundColor};">`,
  )

  // Outer wrapper table
  lines.push(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${styles.backgroundColor};">`,
  )
  lines.push('<tr><td align="center" style="padding:20px 10px;">')

  // Inner content table
  lines.push(
    `<table role="presentation" class="email-container" width="${styles.contentWidth}" cellpadding="0" cellspacing="0" style="max-width:${styles.contentWidth}px;width:100%;background-color:#ffffff;border-radius:8px;overflow:hidden;">`,
  )

  for (const block of blocks) {
    lines.push(renderEmailBlock(block, styles))
  }

  lines.push('</table>')
  lines.push('</td></tr></table>')
  lines.push('</body></html>')

  return lines.join('\n')
}

function renderEmailBlock(
  block: EmailBlock,
  styles: EmailGlobalStyles,
): string {
  const pad = `padding:${block.paddingTop ?? 10}px ${block.paddingRight ?? 20}px ${block.paddingBottom ?? 10}px ${block.paddingLeft ?? 20}px;`
  const bgStyle = block.backgroundColor
    ? `background-color:${block.backgroundColor};`
    : ''

  switch (block.type) {
    case 'text':
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 16}px;line-height:${block.lineHeight || 1.6};color:${block.color || styles.textColor};text-align:${block.align || 'left'};">${block.content || ''}</td></tr>`

    case 'heading':
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 28}px;font-weight:${block.fontWeight || 'bold'};line-height:${block.lineHeight || 1.3};color:${block.color || '#111827'};text-align:${block.align || 'center'};">${block.content || ''}</td></tr>`

    case 'button': {
      const btnBg = block.buttonBgColor || styles.linkColor
      const btnColor = block.buttonTextColor || '#ffffff'
      const btnRadius = block.buttonBorderRadius ?? 6
      const btnPad =
        block.buttonWidth === 'full' ? 'display:block;width:100%;' : ''
      const align = block.align || 'center'
      return `<tr><td style="${pad}${bgStyle}text-align:${align};"><table role="presentation" cellpadding="0" cellspacing="0" style="${align === 'center' ? 'margin:0 auto;' : align === 'right' ? 'margin-left:auto;' : ''}${block.buttonWidth === 'full' ? 'width:100%;' : ''}"><tr><td style="background-color:${btnBg};border-radius:${btnRadius}px;text-align:center;"><a href="${block.buttonUrl || '#'}" target="_blank" style="${btnPad}padding:12px 28px;font-family:${styles.fontFamily};font-size:16px;font-weight:600;color:${btnColor};text-decoration:none;display:inline-block;border-radius:${btnRadius}px;">${block.buttonText || 'Click Here'}</a></td></tr></table></td></tr>`
    }

    case 'image': {
      if (!block.src)
        return `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};"><div style="background:#f3f4f6;border:2px dashed #d1d5db;padding:40px;text-align:center;color:#9ca3af;font-family:${styles.fontFamily};font-size:14px;">Image placeholder</div></td></tr>`
      const imgWidth = block.imageWidth
        ? `width:${block.imageWidth}%;max-width:${styles.contentWidth}px;`
        : `max-width:100%;`
      const wrapStyle = block.href ? '' : ''
      const imgTag = `<img src="${block.src}" alt="${block.alt || ''}" style="${imgWidth}height:auto;display:block;${block.align === 'center' ? 'margin:0 auto;' : ''}" />`
      const content = block.href
        ? `<a href="${block.href}" target="_blank">${imgTag}</a>`
        : imgTag
      return `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};">${content}</td></tr>`
    }

    case 'video_thumbnail': {
      const thumbSrc = block.src || getYouTubeThumbnail(block.videoUrl || '')
      const playOverlay = `<div style="position:relative;display:inline-block;"><img src="${thumbSrc || ''}" alt="${block.alt || 'Watch Video'}" style="max-width:100%;height:auto;display:block;border-radius:8px;" /><div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:60px;height:60px;background:rgba(0,0,0,0.7);border-radius:50%;display:flex;align-items:center;justify-content:center;"><div style="width:0;height:0;border-top:12px solid transparent;border-bottom:12px solid transparent;border-left:20px solid #fff;margin-left:4px;"></div></div></div>`
      const wrapped = block.videoUrl
        ? `<a href="${block.videoUrl}" target="_blank" style="text-decoration:none;">${playOverlay}</a>`
        : playOverlay
      return `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};">${wrapped}</td></tr>`
    }

    case 'divider':
      return `<tr><td style="${pad}${bgStyle}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="border-top:${block.dividerThickness || 1}px ${block.dividerStyle || 'solid'} ${block.dividerColor || '#e5e7eb'};font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>`

    case 'spacer':
      return `<tr><td style="height:${block.height || 20}px;font-size:0;line-height:0;${bgStyle}">&nbsp;</td></tr>`

    case 'columns': {
      const cols = block.columns || [[], []]
      const ratio = block.columnRatio || '50-50'
      const widths = ratioToWidths(ratio)
      let colHtml =
        '<tr><td style="' +
        pad +
        bgStyle +
        '"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>'
      for (let i = 0; i < cols.length; i++) {
        colHtml += `<td class="stack-col" width="${widths[i]}%" valign="top" style="vertical-align:top;">`
        colHtml += `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">`
        for (const childBlock of cols[i]) {
          colHtml += renderEmailBlock(childBlock, styles)
        }
        if (cols[i].length === 0) {
          colHtml += `<tr><td style="padding:20px;text-align:center;color:#9ca3af;font-family:${styles.fontFamily};font-size:13px;">Drop blocks here</td></tr>`
        }
        colHtml += '</table></td>'
      }
      colHtml += '</tr></table></td></tr>'
      return colHtml
    }

    case 'social': {
      const links = block.socialLinks || []
      const size = block.socialIconSize || 32
      let socialHtml = `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};"><table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 auto;"><tr>`
      for (const link of links) {
        const iconUrl = getSocialIconUrl(link.platform, size)
        socialHtml += `<td style="padding:0 6px;"><a href="${link.url || '#'}" target="_blank"><img src="${iconUrl}" alt="${link.platform}" width="${size}" height="${size}" style="display:block;border-radius:4px;" /></a></td>`
      }
      socialHtml += '</tr></table></td></tr>'
      return socialHtml
    }

    default:
      return ''
  }
}

function ratioToWidths(ratio: string): number[] {
  switch (ratio) {
    case '50-50':
      return [50, 50]
    case '33-67':
      return [33, 67]
    case '67-33':
      return [67, 33]
    case '33-33-33':
      return [33, 33, 34]
    default:
      return [50, 50]
  }
}

function getYouTubeThumbnail(url: string): string {
  if (!url) return ''
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/,
  )
  if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
  return ''
}

function getSocialIconUrl(platform: string, size: number): string {
  // Use simple colored placeholder icons via a public CDN
  const colors: Record<string, string> = {
    facebook: '1877F2',
    twitter: '1DA1F2',
    instagram: 'E4405F',
    linkedin: '0A66C2',
    youtube: 'FF0000',
    tiktok: '000000',
    pinterest: 'BD081C',
    whatsapp: '25D366',
  }
  const color = colors[platform.toLowerCase()] || '6b7280'
  return `https://img.shields.io/badge/-${platform}-${color}?style=flat-square&logo=${platform}&logoColor=white&labelColor=${color}`
}
